import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Container,
  AppBar,
  Toolbar,
  Paper,
  Grid,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search,
  KeyboardArrowDown,
  HelpOutline,
  ArrowBack,
  TrendingUp,
  Security,
  Payment,
  Support,
  AccountCircle,
  Speed,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const FAQsPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const faqs = [
    // 1. Payments & Transactions
    {
      question: "Why was my money debited but the payment failed?",
      answer: "This may be due to technical or banking delays. We recommend reaching out to the merchant's support team first. If the issue remains unresolved, you can raise a grievance with us.",
      category: "Payments & Transactions",
      tags: ["payment", "failure", "debit"],
      icon: <Payment sx={{ fontSize: 20 }} />
    },
    {
      question: "I completed the payment but the merchant says they haven't received it. What should I do?",
      answer: "We recommend that you first reach out to the merchant's support team to confirm the status of your payment. However, if the merchant is unable to trace the payment or provide a resolution, please raise a grievance with us. Once we receive the details, we will initiate an investigation from our end and assist you further.",
      category: "Payments & Transactions",
      tags: ["payment", "merchant", "grievance"],
      icon: <Payment sx={{ fontSize: 20 }} />
    },
    
    // 2. Refunds
    {
      question: "When will I receive my refund?",
      answer: "Refunds are typically initiated by the merchant and are processed within 8-11 working days. Please contact the merchant first to confirm the status of the refund. If they've already processed it and you haven't received the amount, you may raise a ticket with us.",
      category: "Refunds",
      tags: ["refund", "timeline", "merchant"],
      icon: <TrendingUp sx={{ fontSize: 20 }} />
    },
    {
      question: "The refund is marked as successful but I haven't received the amount?",
      answer: "We recommend confirming with the merchant whether the refund has been successfully initiated from their end. If the merchant confirms that the refund was processed, please check with your bank to see if the amount has been credited to your account. Refunds usually take 8-11 working days to reflect, depending on your bank's processing timelines. If the refund still doesn't appear in your account after this period, please raise a grievance with us by sharing the necessary details so that we can investigate the issue further and assist you accordingly.",
      category: "Refunds",
      tags: ["refund", "bank", "investigation"],
      icon: <TrendingUp sx={{ fontSize: 20 }} />
    },
    
    // 3. Unauthorized / Fraudulent Transactions
    {
      question: "I didn't authorize a transaction. What should I do?",
      answer: "Please confirm with any connected apps or family members. If the transaction is truly unauthorized, report it to the merchant first and ask for the transaction details. If you still feel this transaction should not have been charged to you, raise a grievance with us by sharing the transaction details.",
      category: "Unauthorized / Fraudulent Transactions",
      tags: ["unauthorized", "fraud", "grievance"],
      icon: <Security sx={{ fontSize: 20 }} />
    },
    {
      question: "How do I report a potential fraud or unauthorized transaction to PB Pay?",
      answer: "We recommend contacting the merchant first and reporting a fraud, if no resolution is received. Please contact your bank and raise a dispute against the transaction.",
      category: "Unauthorized / Fraudulent Transactions",
      tags: ["fraud", "dispute", "bank"],
      icon: <Security sx={{ fontSize: 20 }} />
    },
    
    // 4. Merchant/Product Related
    {
      question: "I paid but didn't receive the product/service. What should I do?",
      answer: "Please contact the merchant directly for product or service-related issues. PB Pay helps process the payment - the merchant is responsible for delivering the service/product. Raise a grievance with us only if the merchant is unresponsive.",
      category: "Merchant/Product Related",
      tags: ["product", "service", "merchant"],
      icon: <Support sx={{ fontSize: 20 }} />
    },
    {
      question: "I want to cancel my order and get a refund. Can PB Pay help?",
      answer: "Order cancellation and refunds are managed entirely by the merchant. Please reach out to them to process your request. If the refund is delayed after merchant confirmation, you may contact us with transaction details.",
      category: "Merchant/Product Related",
      tags: ["cancellation", "refund", "merchant"],
      icon: <Support sx={{ fontSize: 20 }} />
    },
    
    // 5. Support & Complaint Resolution
    {
      question: "How long will it take to resolve my complaint?",
      answer: "Resolution timelines may vary depending on the nature of the issue. However, most queries are typically resolved within 10 business days. If you do not receive a resolution within this timeframe, you may escalate the matter as per the escalation matrix outlined in our Grievance Redressal Policy.",
      category: "Support & Complaint Resolution",
      tags: ["resolution", "timeline", "escalation"],
      icon: <Support sx={{ fontSize: 20 }} />
    },
    {
      question: "How will I be updated on my grievance?",
      answer: "Once you raise a complaint with us, you'll receive updates via email at key stages. You can also track the status in the PB Pay grievance dashboard using your reference number by logging in with your email id through which you have registered the grievance.",
      category: "Support & Complaint Resolution",
      tags: ["updates", "email", "dashboard"],
      icon: <Support sx={{ fontSize: 20 }} />
    },
  ];

  const categories = [
    { name: "All", icon: <HelpOutline />, count: faqs.length },
    { name: "Payments & Transactions", icon: <Payment />, count: faqs.filter(f => f.category === "Payments & Transactions").length },
    { name: "Refunds", icon: <TrendingUp />, count: faqs.filter(f => f.category === "Refunds").length },
    { name: "Unauthorized / Fraudulent Transactions", icon: <Security />, count: faqs.filter(f => f.category === "Unauthorized / Fraudulent Transactions").length },
    { name: "Merchant/Product Related", icon: <Support />, count: faqs.filter(f => f.category === "Merchant/Product Related").length },
    { name: "Support & Complaint Resolution", icon: <Support />, count: faqs.filter(f => f.category === "Support & Complaint Resolution").length },
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === "All" || faq.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    // Removed the line that was clearing search query
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#FAFBFC" }}>
      {/* Header */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: "#FFFFFF",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderBottom: "1px solid #E1E5E9"
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleBack}
            sx={{ 
              mr: 2,
              color: "#253858",
              "&:hover": { backgroundColor: "#F4F5F7" }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#253858", flexGrow: 1 }}>
            Help Center
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ 
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
        py: 6,
        position: "relative",
        overflow: "hidden"
      }}>
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box sx={{ textAlign: "center", position: "relative", zIndex: 2 }}>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  fontSize: { xs: "2rem", md: "3rem" }
                }}
              >
                How can we help you?
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 4, 
                  opacity: 0.9,
                  fontSize: { xs: "1rem", md: "1.25rem" }
                }}
              >
                Find answers to common questions about payments, refunds, and more
              </Typography>
            </Box>
          </Fade>
        </Container>
        
        {/* Background decoration */}
        <Box sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grain\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"><circle cx=\"25\" cy=\"25\" r=\"1\" fill=\"white\" opacity=\"0.1\"/><circle cx=\"75\" cy=\"75\" r=\"1\" fill=\"white\" opacity=\"0.1\"/><circle cx=\"50\" cy=\"10\" r=\"0.5\" fill=\"white\" opacity=\"0.1\"/><circle cx=\"10\" cy=\"60\" r=\"0.5\" fill=\"white\" opacity=\"0.1\"/><circle cx=\"90\" cy=\"40\" r=\"0.5\" fill=\"white\" opacity=\"0.1\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grain)\"/></svg>')",
          opacity: 0.3
        }} />
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Category Filter */}
        <Slide direction="up" in timeout={1000}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#253858" }}>
              Browse by category
            </Typography>
            <Grid container spacing={2}>
              {categories.map((category) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={category.name}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: "center",
                      cursor: "pointer",
                      borderRadius: "12px",
                      border: "2px solid",
                      borderColor: selectedCategory === category.name ? "#667eea" : "#E1E5E9",
                      backgroundColor: selectedCategory === category.name ? "#F0F4FF" : "#FFFFFF",
                      transition: "all 0.3s ease",
                      height: "140px", // Fixed height for all cards
                      width: "100%", // Full width of grid item
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      "&:hover": {
                        borderColor: "#667eea",
                        backgroundColor: "#F0F4FF",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 20px rgba(102, 126, 234, 0.15)"
                      }
                    }}
                    onClick={() => handleCategorySelect(category.name)}
                  >
                    <Box sx={{ 
                      color: selectedCategory === category.name ? "#667eea" : "#5E6C84",
                      mb: 1,
                      transition: "color 0.3s ease",
                      flexShrink: 0
                    }}>
                      {category.icon}
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: selectedCategory === category.name ? "#667eea" : "#253858",
                        mb: 0.5,
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {category.name}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: "#5E6C84",
                        backgroundColor: "#F4F5F7",
                        px: 1,
                        py: 0.5,
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        flexShrink: 0
                      }}
                    >
                      {category.count}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Slide>

        {/* Search Results */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ color: "#5E6C84", mb: 2 }}>
            {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} found
            {searchQuery && ` for "${searchQuery}"`}
            {selectedCategory !== "All" && ` in ${selectedCategory}`}
          </Typography>
        </Box>

        {/* FAQ List */}
        <Fade in timeout={1200}>
          <Box>
            {filteredFaqs.map((faq, index) => (
              <Slide direction="up" in timeout={800 + index * 100} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2,
                    borderRadius: "16px",
                    border: "1px solid #E1E5E9",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                      borderColor: "#667eea",
                      transform: "translateY(-2px)"
                    }
                  }}
                >
                  <Accordion
                    expanded={openIndex === index}
                    onChange={() => toggleFAQ(index)}
                    sx={{
                      "&:before": { display: "none" },
                      boxShadow: "none",
                      backgroundColor: "transparent"
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <KeyboardArrowDown
                          sx={{
                            transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                            color: "#667eea",
                            fontSize: "1.5rem"
                          }}
                        />
                      }
                      sx={{
                        padding: "24px",
                        backgroundColor: openIndex === index ? "#F8F9FA" : "#FFFFFF",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "#F8F9FA",
                        },
                        "& .MuiAccordionSummary-content": {
                          margin: 0
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flex: 1 }}>
                        <Box sx={{ 
                          color: "#667eea",
                          mt: 0.5
                        }}>
                          {faq.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "1.1rem",
                              lineHeight: 1.4,
                              color: "#253858",
                              mb: 1
                            }}
                          >
                            {faq.question}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Chip
                              label={faq.category}
                              size="small"
                              sx={{
                                backgroundColor: "#E3F2FD",
                                color: "#1976D2",
                                fontWeight: 500,
                                fontSize: "0.75rem",
                                height: "24px"
                              }}
                            />
                            {faq.tags.slice(0, 2).map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{
                                  backgroundColor: "#F4F5F7",
                                  color: "#5E6C84",
                                  fontWeight: 400,
                                  fontSize: "0.75rem",
                                  height: "24px"
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        padding: "0 24px 24px 56px",
                        backgroundColor: "#F8F9FA",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          color: "#5E6C84",
                          lineHeight: 1.6,
                          fontWeight: 400,
                        }}
                      >
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </Slide>
            ))}
          </Box>
        </Fade>

        {filteredFaqs.length === 0 && (
          <Fade in timeout={800}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Box sx={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                backgroundColor: "#F4F5F7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3
              }}>
                <HelpOutline sx={{ fontSize: 48, color: "#C1C7D0" }} />
              </Box>
              <Typography sx={{ color: "#253858", fontSize: "1.5rem", fontWeight: 600, mb: 1 }}>
                No results found
              </Typography>
              <Typography sx={{ color: "#5E6C84", fontSize: "1rem", mb: 3 }}>
                {searchQuery 
                  ? `We couldn't find any FAQs matching "${searchQuery}"`
                  : "Try searching with different keywords or browse all categories"
                }
              </Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                <Chip
                  label="Clear search"
                  onClick={() => setSearchQuery("")}
                  sx={{
                    backgroundColor: "#667eea",
                    color: "white",
                    fontWeight: 500,
                    "&:hover": { backgroundColor: "#5a6fd8" }
                  }}
                />
                <Chip
                  label="View all FAQs"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("All");
                  }}
                  sx={{
                    backgroundColor: "#F4F5F7",
                    color: "#253858",
                    fontWeight: 500,
                    "&:hover": { backgroundColor: "#E1E5E9" }
                  }}
                />
              </Box>
            </Box>
          </Fade>
        )}
      </Container>
    </Box>
  );
};

export default FAQsPage;